import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { AppConfig } from '@config/app.config';
import { AccountingGroupsService } from '@core/services/accounting-groups.service';
import { GeneralsService } from '@core/services/generals.service';
import { RulesService } from '@core/services/rules.service';
import {
  AccountingGroupsEditComponent,
} from '@modules/accounting-groups/accounting-groups-edit/accounting-groups-edit.component';
import { Constants } from '@shared/class/constants.class';
import { AccountingGroup } from '@shared/interfaces/accounting-group';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-accounting-groups-list',
  templateUrl: './accounting-groups-list.component.html'
})
export class AccountingGroupsListComponent implements OnInit, OnDestroy {
  public constants = new Constants();

  public location = 'accountingGroups';
  public accountingGroupsList: Array<AccountingGroup> = [];
  public accountingGroup: AccountingGroup;
  public dataSource: MatTableDataSource<AccountingGroup>;
  public selection = new SelectionModel<AccountingGroup>(true, []);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filter') filterInput: ElementRef;

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    public accountingGroupsService: AccountingGroupsService,
    public rulesService: RulesService
  ) {
    this.constants.displayedColumns = [
      this.appConfig.ACTIONS.delete.id,
      'id',
      'name',
      'idRule',
      'rule',
      this.appConfig.ACTIONS.edit.id
    ];
  }

  ngOnInit() {
    this.generalsService.action$
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result) {
          const actions = this.generalsService.getActions(
            this.constants.displayedColumns,
            this.constants.actionsList,
            this.location
          );
          this.constants.displayedColumns = actions.displayedColumns;
          this.constants.displayedActions = actions.displayedActions;
          this.loadData();
        }
      });
  }

  ngOnDestroy() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  loadData() {
    this.constants.loading = true;
    this.constants.elements = true;
    this.accountingGroupsService
      .getItems()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          this.accountingGroupsList = [];
          result.data.forEach((element: any) => {
            this.accountingGroup = {
              id: element.id,
              name: element.attributes.name,
              idRule: element.relationships.rule.id,
              rule: element.relationships.rule.attributes.name
            };
            this.accountingGroupsList.push(this.accountingGroup);
          });
          if (this.accountingGroupsList.length) {
            this.dataSource = new MatTableDataSource(this.accountingGroupsList);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.constants.filter = this.dataSource.filteredData;
            this.selection.clear();
          } else {
            this.constants.elements = false;
          }

          this.constants.error = false;
          this.constants.loading = false;
        },
        error => {
          this.constants.errorText = this.generalsService.showError(error);
          this.constants.error = true;
          this.constants.loading = false;
        }
      );
  }

  reload() {
    this.generalsService.reload(this);
  }

  edit(action: string, item?: AccountingGroup) {
    let dialog: any;

    const data = {
      action: action,
      item: {},
      relationships: {}
    };

    const loading = this.generalsService.loading();
    this.rulesService
      .getItems()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          loading.close();
          if (!result.data || result.data.length <= 0) {
            this.generalsService.notification(
              this.appConfig.NOTIFICATION.error,
              this.appConfig.LANG.amlLimits.noSegments
            );
          } else {
            if (action === this.appConfig.ACTIONS.add.id) {
              this.constants.message = this.appConfig.LANG.message.addSuccess;
            } else {
              data.item = item;
              this.constants.message = this.appConfig.LANG.message.editSuccess;
            }

            data.relationships = result.data;
            dialog = this.generalsService.modal(
              AccountingGroupsEditComponent,
              data
            );

            dialog
              .afterClosed()
              .pipe(takeUntil(this.constants.destroy$))
              .subscribe((afterClosedResult: any) => {
                this.generalsService.afterClosed(
                  this,
                  afterClosedResult,
                  this.constants.message
                );
              });
          }
        },
        error => {
          loading.close();
          this.generalsService.notification(
            this.appConfig.NOTIFICATION.error,
            this.generalsService.showError(error)
          );
        }
      );
  }

  selectDelete() {
    const numSelected = this.selection.selected.length,
      numRows = this.dataSource.data.length;

    this.constants.labelDelete = this.generalsService.messageSelectedDelete(
      'label',
      numSelected,
      numRows
    );
  }

  filterData(event: any) {
    this.generalsService.applyFilter(this.dataSource, event);
    this.constants.filter = this.dataSource.filteredData;
  }
}
